import React from 'react';
import { Container, Row, Col, Card, Button, Carousel } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const Home = () => {
  const features = [
    { icon: 'fas fa-graduation-cap', title: 'Quality Education', desc: 'NAAC Accredited Institution with excellent infrastructure' },
    { icon: 'fas fa-chalkboard-teacher', title: 'Expert Faculty', desc: '200+ highly qualified and experienced faculty members' },
    { icon: 'fas fa-flask', title: 'Modern Labs', desc: 'State-of-the-art laboratories with latest equipment' },
    { icon: 'fas fa-book', title: 'Rich Library', desc: '50,000+ books, journals and digital resources' },
    { icon: 'fas fa-laptop-code', title: 'Digital Campus', desc: 'Wi-Fi enabled campus with smart classrooms' },
    { icon: 'fas fa-briefcase', title: 'Placements', desc: '90% placement record with top companies' }
  ];

  const stats = [
    { value: '5000+', label: 'Students Enrolled', color: 'text-primary' },
    { value: '200+', label: 'Faculty Members', color: 'text-success' },
    { value: '50+', label: 'Courses Offered', color: 'text-warning' },
    { value: '25+', label: 'Years of Excellence', color: 'text-info' }
  ];

  const quickLinks = [
    { title: 'Admissions 2024-25', link: '/admission', icon: 'fas fa-user-graduate' },
    { title: 'Download Prospectus', link: '/prospectus', icon: 'fas fa-download' },
    { title: 'Academic Calendar', link: '/calendar', icon: 'fas fa-calendar-alt' },
    { title: 'Online Payment', link: '/payment', icon: 'fas fa-credit-card' }
  ];

  const slides = [
    {
      image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
      title: 'Excellence in Education Since 1990',
      subtitle: 'NAAC Accredited Grade "A" Institution',
      buttonText: 'Explore Programs',
      buttonLink: '/courses'
    },
    {
      image: 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
      title: 'State-of-the-Art Infrastructure',
      subtitle: 'Modern labs, library and digital classrooms',
      buttonText: 'Campus Tour',
      buttonLink: '/campus'
    },
    {
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
      title: 'Placement Opportunities',
      subtitle: '90% placement record with top companies',
      buttonText: 'Placement Cell',
      buttonLink: '/placement'
    }
  ];

  return (
    <>
      {/* Hero Carousel Section */}
      <section className="hero-carousel" style={{ marginTop: '-70px', paddingTop: '70px' }}>
        <Carousel fade controls indicators interval={4000}>
          {slides.map((slide, index) => (
            <Carousel.Item key={index}>
              <div 
                className="carousel-slide"
                style={{
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${slide.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: '85vh',
                  minHeight: '600px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Container>
                  <Row className="align-items-center">
                    <Col lg={8} className="text-white">
                      <h1 className="display-3 fw-bold mb-4 animate__animated animate__fadeInUp">
                        {slide.title}
                      </h1>
                      <p className="lead mb-4 fs-4 animate__animated animate__fadeInUp animate__delay-1s">
                        {slide.subtitle}
                      </p>
                      <Button 
                        as={Link} 
                        to={slide.buttonLink}
                        variant="warning" 
                        size="lg" 
                        className="px-5 py-3 animate__animated animate__fadeInUp animate__delay-2s"
                      >
                        <i className="fas fa-arrow-right me-2"></i>
                        {slide.buttonText}
                      </Button>
                    </Col>
                  </Row>
                </Container>
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
      </section>

      {/* Quick Stats Bar */}
      <section className="py-4 bg-primary text-white">
        <Container>
          <Row className="text-center">
            {stats.map((stat, index) => (
              <Col md={3} key={index} className="mb-3 mb-md-0">
                <div className="stat-item">
                  <h2 className={`fw-bold mb-2 ${stat.color}`}>{stat.value}</h2>
                  <p className="mb-0">{stat.label}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Quick Links */}
      <section className="py-5 bg-light">
        <Container>
          <div className="text-center mb-4">
            <h2 className="fw-bold text-primary">Quick Links</h2>
            <p className="text-muted">Access important information quickly</p>
          </div>
          <Row className="g-4">
            {quickLinks.map((link, index) => (
              <Col lg={3} md={6} key={index}>
                <Card as={Link} to={link.link} 
                      className="text-center border-0 shadow-sm h-100 text-decoration-none text-dark quick-link-card">
                  <Card.Body className="py-4 px-3">
                    <div className="quick-link-icon mb-3">
                      <i className={`${link.icon} fa-3x text-primary`}></i>
                    </div>
                    <Card.Title className="mb-0 fw-bold">{link.title}</Card.Title>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold text-primary mb-3">Why Choose Our College</h2>
            <p className="lead text-muted">We provide an environment that fosters academic excellence and personal growth</p>
          </div>
          <Row>
            {features.map((feature, index) => (
              <Col lg={4} md={6} className="mb-4" key={index}>
                <Card className="h-100 border-0 shadow-sm feature-card">
                  <Card.Body className="p-4 text-center">
                    <div className="feature-icon mb-4">
                      <div className="rounded-circle bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center" 
                           style={{ width: '80px', height: '80px' }}>
                        <i className={`${feature.icon} fa-2x text-primary`}></i>
                      </div>
                    </div>
                    <Card.Title className="mb-3 fw-bold">{feature.title}</Card.Title>
                    <Card.Text className="text-muted">{feature.desc}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* About College Section */}
      <section className="py-5 bg-dark text-white">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <h2 className="display-6 fw-bold mb-4">About University College</h2>
              <p className="lead mb-4">
                Established in 1990, University College has been a beacon of excellence in higher education. 
                Our commitment to academic rigor, innovative teaching methods, and holistic development 
                has made us one of the premier educational institutions in the region.
              </p>
              <p className="mb-4">
                With state-of-the-art infrastructure, experienced faculty, and a vibrant campus life, 
                we provide students with the perfect environment to excel academically and personally.
              </p>
              <Button as={Link} to="/about" variant="outline-light" size="lg" className="px-4">
                Learn More About Us
              </Button>
            </Col>
            <Col lg={6}>
              <div className="position-relative">
                <img 
                  src="https://images.unsplash.com/photo-1562774053-701939374585?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="College Campus" 
                  className="img-fluid rounded shadow-lg"
                />
                <div className="position-absolute bottom-0 start-0 bg-warning text-dark p-3 rounded-end" 
                     style={{ transform: 'translateY(50%)' }}>
                  <h5 className="mb-0">ISO 9001:2015 Certified</h5>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Notice Board & Events */}
      <section className="py-5">
        <Container>
          <Row className="g-4">
            <Col lg={8}>
              <h3 className="mb-4 fw-bold border-bottom pb-2">Latest Announcements</h3>
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                  <div className="d-flex align-items-start mb-3">
                    <div className="bg-danger rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                         style={{ width: '50px', height: '50px' }}>
                      <i className="fas fa-bullhorn text-white fa-lg"></i>
                    </div>
                    <div>
                      <h5 className="mb-1 text-danger">Admissions Open for 2024-25</h5>
                      <small className="text-muted d-block mb-2">
                        <i className="fas fa-calendar-alt me-1"></i> December 15, 2024
                      </small>
                      <p className="mb-0">
                        Online applications are now open for all undergraduate and postgraduate programs. 
                        Last date for submission: January 31, 2025. Apply early for scholarship opportunities.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-start">
                    <div className="bg-success rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                         style={{ width: '50px', height: '50px' }}>
                      <i className="fas fa-newspaper text-white fa-lg"></i>
                    </div>
                    <div>
                      <h5 className="mb-1 text-success">Mid-Term Examinations Schedule</h5>
                      <small className="text-muted d-block mb-2">
                        <i className="fas fa-calendar-alt me-1"></i> December 18, 2024
                      </small>
                      <p className="mb-0">
                        Mid-term examinations for all courses will be conducted from January 15-30, 2025. 
                        Detailed timetable will be published on the notice board and college website.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={4}>
              <h3 className="mb-4 fw-bold border-bottom pb-2">Upcoming Events</h3>
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body className="p-4">
                  <div className="event-item mb-4">
                    <div className="d-flex align-items-start">
                      <div className="bg-primary text-white text-center p-2 rounded me-3 flex-shrink-0" 
                           style={{ minWidth: '70px' }}>
                        <div className="fw-bold fs-4">25</div>
                        <div className="small text-uppercase">DEC</div>
                        <div className="small">2024</div>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">Annual Sports Day</h6>
                        <small className="text-muted d-block mb-1">
                          <i className="fas fa-map-marker-alt me-1"></i> College Ground
                        </small>
                        <small className="text-muted">
                          <i className="fas fa-clock me-1"></i> 9:00 AM - 5:00 PM
                        </small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="event-item mb-4">
                    <div className="d-flex align-items-start">
                      <div className="bg-success text-white text-center p-2 rounded me-3 flex-shrink-0" 
                           style={{ minWidth: '70px' }}>
                        <div className="fw-bold fs-4">30</div>
                        <div className="small text-uppercase">DEC</div>
                        <div className="small">2024</div>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">Cultural Fest - "Kaleidoscope 2024"</h6>
                        <small className="text-muted d-block mb-1">
                          <i className="fas fa-map-marker-alt me-1"></i> Main Auditorium
                        </small>
                        <small className="text-muted">
                          <i className="fas fa-clock me-1"></i> 10:00 AM onwards
                        </small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="event-item">
                    <div className="d-flex align-items-start">
                      <div className="bg-warning text-white text-center p-2 rounded me-3 flex-shrink-0" 
                           style={{ minWidth: '70px' }}>
                        <div className="fw-bold fs-4">05</div>
                        <div className="small text-uppercase">JAN</div>
                        <div className="small">2025</div>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">Industry Expert Guest Lecture</h6>
                        <small className="text-muted d-block mb-1">
                          <i className="fas fa-map-marker-alt me-1"></i> Seminar Hall
                        </small>
                        <small className="text-muted">
                          <i className="fas fa-clock me-1"></i> 2:00 PM - 4:00 PM
                        </small>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="py-5 bg-primary text-white">
        <Container className="text-center">
          <h2 className="display-5 fw-bold mb-4">Start Your Educational Journey Today</h2>
          <p className="lead mb-4 opacity-75 fs-5">
            Join our vibrant community of learners, innovators, and future leaders. 
            Transform your dreams into reality with quality education.
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-3">
            <Button as={Link} to="/admission" variant="light" size="lg" className="px-5 py-3 fw-bold">
              <i className="fas fa-file-alt me-2"></i> Apply for Admission
            </Button>
            <Button as={Link} to="/contact" variant="outline-light" size="lg" className="px-5 py-3 fw-bold">
              <i className="fas fa-phone-alt me-2"></i> Contact Admissions
            </Button>
            <Button as={Link} to="/virtual-tour" variant="outline-light" size="lg" className="px-5 py-3 fw-bold">
              <i className="fas fa-video me-2"></i> Virtual Campus Tour
            </Button>
          </div>
        </Container>
      </section>

      <style jsx="true">{`
        /* Fix header overlap */
        .hero-carousel {
          margin-top: -70px;
          padding-top: 70px;
        }
        
        /* Carousel custom styles */
        .carousel-slide {
          position: relative;
          animation: fadeIn 1s ease-in;
        }
        
        .carousel-control-prev,
        .carousel-control-next {
          width: 5%;
        }
        
        .carousel-indicators button {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          margin: 0 5px;
        }
        
        /* Quick link cards */
        .quick-link-card {
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }
        
        .quick-link-card:hover {
          transform: translateY(-10px);
          border-color: var(--bs-primary);
          box-shadow: 0 10px 20px rgba(0,0,0,0.15) !important;
        }
        
        .quick-link-icon {
          transition: transform 0.3s ease;
        }
        
        .quick-link-card:hover .quick-link-icon {
          transform: scale(1.1);
        }
        
        /* Feature cards */
        .feature-card {
          transition: all 0.3s ease;
        }
        
        .feature-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
        }
        
        .feature-card:hover .feature-icon {
          transform: scale(1.1);
        }
        
        .feature-icon {
          transition: transform 0.3s ease;
        }
        
        /* Stats animation */
        .stat-item {
          transition: transform 0.3s ease;
        }
        
        .stat-item:hover {
          transform: scale(1.05);
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .carousel-slide {
            height: 60vh;
            min-height: 400px;
          }
          
          .hero-carousel h1 {
            font-size: 2.5rem;
          }
          
          .hero-carousel .lead {
            font-size: 1.2rem;
          }
        }
        
        @media (max-width: 576px) {
          .carousel-slide {
            height: 50vh;
            min-height: 350px;
          }
          
          .hero-carousel h1 {
            font-size: 2rem;
          }
          
          .hero-carousel .lead {
            font-size: 1rem;
          }
        }
        
        /* Animation keyframes */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default Home;